/**
 * Scenario Layer Detail Modal
 * Shows detailed information about a scenario layer including properties and production plan
 */
import {
    Close,
    Storage,
} from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Typography
} from '@mui/material';
import React from 'react';

import { ScenarioLayer } from '@/api/scenarioLayers';
import { ScenarioLayerPropertiesByCategory } from './ScenarioLayerPropertiesByCategory';

interface ScenarioLayerDetailModalProps {
  open: boolean;
  onClose: () => void;
  layer: ScenarioLayer | null;
  readOnly?: boolean;
}

export const ScenarioLayerDetailModal: React.FC<ScenarioLayerDetailModalProps> = ({
  open,
  onClose,
  layer,
  readOnly = false,
}) => {
  if (!layer) return null;

  // Debug: log resource data
  React.useEffect(() => {
    if (layer) {
      console.log('ScenarioLayerDetailModal - layer.resource:', layer.resource);
    }
  }, [layer]);

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'oil':
        return 'success';
      case 'gas':
        return 'info';
      case 'oil and gas':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Storage color="primary" />
          <Typography variant="h6">
            Reservoir Details: {layer.layer_name}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Reservoir Name
                  </Typography>
                  <Typography variant="body1">
                    {layer.layer_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Type
                  </Typography>
                  {layer.layer?.type && (
                    <Chip
                      label={layer.layer.type}
                      color={getTypeColor(layer.layer.type) as any}
                      size="small"
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Source
                  </Typography>
                  <Typography variant="body1">
                    {layer.ref_layer && 'Existing Reservoir'}
                    {layer.ref_layer_template && 'Reservoir Template'}
                    {!layer.ref_layer && !layer.ref_layer_template && 'New Reservoir'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Field
                  </Typography>
                  <Typography variant="body1">
                    {layer.field?.name || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Reservoir Properties */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Reservoir Properties
              </Typography>
              <Box sx={{ maxHeight: 600, overflow: 'auto', mt: 2 }}>
                <ScenarioLayerPropertiesByCategory properties={layer.properties} />
              </Box>
            </Paper>
          </Grid>

          {/* Resource Data */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Resource Data
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Initial Geological (thousand t)
                  </Typography>
                  <Typography variant="body1">
                    {layer.resource?.initial_geological !== null && layer.resource?.initial_geological !== undefined
                      ? layer.resource.initial_geological.toLocaleString()
                      : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
